<template>
  <div class="container-fluid p-0">
    <!-- Título principal -->
    <h2 class="mb-4">Lista de Empleados</h2>

    <!-- Card de filtros -->
    <b-card class="mb-3" style="max-width: 100vw;">
      <b-card-header
        role="button"
        tabindex="0"
        @click="toggleCollapse"
        @keydown.enter.prevent="toggleCollapse"
        @keydown.space.prevent="toggleCollapse"
        class="btn btn-sm btn-primary d-inline-flex align-items-center gap-2 px-3 py-1"
        style="width: auto;"
      >
        <span>Filtros</span>
        <feather-icon
          icon="ChevronDownIcon"
          :class="{ 'rotate-180': isCollapsed }"
          class="transition-transform"
        />
      </b-card-header>

      <b-collapse v-model="isCollapsed">
        <b-row class="mt-2 g-0">
          <!-- Sort -->
          <b-col cols="12" md="2" class="mb-2">
            <b-form-group
              label-cols-sm="2"
              label-align-sm="left"
              label-for="sortBySelect">
              <h5>Ordenar</h5>
              <b-input-group>
                <b-form-select
                  id="sortBySelect"
                  v-model="sortBy"
                  :options="sortOptions"
                  class="mr-1"
                  style="min-width: 50%">
                  <template #first>
                    <option value="">Ninguno</option>
                  </template>
                </b-form-select>
                <b-form-select
                  v-model="sortDesc"
                  :disabled="!sortBy"
                  style="min-width: 40%">
                  <option :value="false">Asc</option>
                  <option :value="true">Desc</option>
                </b-form-select>
              </b-input-group>
            </b-form-group>
          </b-col>

          <!-- Filtros dinámicos -->
          <b-col
            v-for="filter in filtersConfig.filter(
              (f) => f.type !== 'date-range'
            )"
            :key="filter.key"
            cols="12"
            md="2">
            <b-form-group>
              <h5>{{ filter.label }}</h5>
              <b-input-group>
                <b-form-input
                  v-model="filters[filter.key]"
                  :type="filter.type || 'search'"
                  :placeholder="filter.placeholder" />
              </b-input-group>
            </b-form-group>
          </b-col>

          <!-- Botones globales de aplicar y limpiar filtros -->
          <b-col
            cols="12"
            class="d-flex align-items-end justify-content-end"
            style="gap: 1rem">
            <div class="text-left">
              <b-button variant="primary" @click="applyFilter">
                <feather-icon icon="FilterIcon" /> Aplicar
              </b-button>
              <b-button
                variant="outline-danger"
                @click="clearAllFilters"
                class="ml-2">
                <feather-icon icon="XCircleIcon" /> Limpiar Todo
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-collapse>

      <!-- Botones "Enviar Dispositius" y "Add" -->
      <div
        class="d-flex justify-content-between align-items-center mt-3"
        v-if="$can('create', 'AddEmployee')">
        <div>
          <h5 class="mb-1"></h5>
          <b-button
            variant="primary"
            @click="actualizarCredenciales()"
            class="btn-icon">
            <feather-icon icon="SendIcon" /> Enviar Dispositivos
          </b-button>
        </div>

        <div class="text-left">
          <h5></h5>
          <b-button
            variant="primary"
            @click="createDraftEmployee"
            class="btn-icon">
            <feather-icon icon="PlusCircleIcon" /> Añadir empleado
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Card de tabla -->
    <b-card style="max-width: 100vw">
      <b-table
        borderless
        striped
        hover
        responsive
        class="position-relative"
        :per-page="perPage"
        :current-page="currentPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn">
        <template #cell(Modify)="data" v-if="$can('edit', 'EditEmployees')">
          <b-button
            variant="flat-primary"
            :to="{
              name: 'edit-employees',
              params: { id: data.item.pkid_accreditation_document_detail },
            }"
            class="btn-icon"
            size="sm">
            <feather-icon icon="EditIcon" size="24" />
          </b-button>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <!-- page length -->
        <b-form-group
          label="Por página"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="sortBySelect"
          class="text-nowrap mb-md-0 mr-1">
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions" />
        </b-form-group>

        <!-- pagination -->
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0">
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import BCardCode from "@core/components/b-card-code/BCardCode.vue";
import {
  BTable,
  BAvatar,
  BBadge,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCardBody,
  BCard,
  BCollapse,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import flatPickr from "vue-flatpickr-component";
import axios from "@/libs/axios";
import { initialAbility } from "@/libs/acl/config";
import { decryptString, encryptString } from "@/libs/crypto";
import moment from "moment";

export default {
  components: {
    BCardCode,
    flatPickr,
    ToastificationContent,
    BTable,
    BAvatar,
    BBadge,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BCardBody,
    BCard,
    BCollapse,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
  },
  computed: {
    sortOptions() {
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },
  methods: {
    filterResults() {
      let request = "/employees?";
      let count = 0;
      let tn = true;
      let an = true;
      let sn = true;
      let dfn = true;
      let dtn = true;

      this.isFetching = true;

      // Filtrar los datos según los campos seleccionados
      this.items = this.initialItems.filter((item) => {});

      // Actualizar el número total de filas después de filtrar
      this.totalRows = this.items.length;

      // Marcar que ya se han terminado los filtros
      this.isFetching = false;

      axios
        .get(request)
        .then((results) => {
          this.items = results.data;
          this.totalRows = this.items.length;
        })
        .catch((error) => {
          const variant = "danger";
          this.$bvToast.toast("Fallo al conectar", {
            title: `` + error,
            variant,
            solid: true,
          });
          if (localStorage.getItem("accessToken") === null) {
            this.$ability.update(initialAbility);
            this.$router.push({ name: "login" });
          }
        });
    },
    showId(id) {},

    async actualizarCredenciales() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Quieres enviar todos los dipositivos?",
        {
          title: "Confirmación",
          size: "md",
          buttonSize: "md",
          okVariant: "primary",
          okTitle: "Sí",
          cancelTitle: "No",
          cancelVariant: "secondary",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );

      if (!confirmed) {
        return; // El usuario dijo que no, no hacer nada
      }

      try {
        const url = process.env.VUE_APP_URL_API_UPDATE_CREDENTIALS;
        await axios.post(url);

        this.$bvToast.toast("Dispositivos enviados", {
          title: "Éxit",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
      } catch (error) {
        console.error("Error completo en axios:", error);
        console.error("Error respuesta:", error.response);
        const backendMessage =
          error.response?.data?.error || "Error al enviar los depositivos";

        this.$bvToast.toast(backendMessage, {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },

    async createDraftEmployee() {
      try {
        // 1. Eliminar empleados temporales antiguos
        await axios.delete("/employeesDelete", {
          params: {
            fkid_accreditation_process_type: 3
          }
        });
        // 2. Crear nuevo empleado temporal
        const response = await axios.post("/employeesDraft", {
          Temporada: this.selectedSeason || "",
          fkid_mxs_event: this.selectedEvent || "",
          fkid_accreditation_process_type: 3,
        });

        const detailId = response.data.detailId;

        // 3. Redirigir a la pantalla de creación/edición
        this.$router.push({
          path: "/employees/create",
          query: { id: detailId },
        });
      } catch (error) {
        console.error("Error al crear empleado temporal:", error);
        this.$toast.error("No se pudo crear el empleado");
      }
    },

    async fetchData() {
      this.isFetching = true;
      try {
        // Construimos los parámetros de paginación
        const params = {};

        if (!this.filters.fkid_accreditation_process_type) {
          params.fkid_accreditation_process_type = 3;
        }
        // Aplicamos el resto de filtros (sin fechas)
        this.filtersConfig.forEach((filter) => {
          if (this.filters[filter.key]) {
            const value = filter.formatter
              ? filter.formatter(this.filters[filter.key])
              : this.filters[filter.key];

            if (value) {
              params[filter.param] = value;
            }
          }
        });

        // Hacemos la llamada a /employees con params (página, límite y demás filtros)
        const response = await axios.get("/employees", { params });

        // si no, asumimos que retorna directamente un array.
        if (response.data && response.data.data) {
          this.items = response.data.data;
          this.totalRows = response.data.totalCount;
        } else {
          this.items = response.data;
          this.totalRows = this.items.length;
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
        this.items = [];
        this.totalRows = 0;
        this.$bvToast.toast("Fallo al conectar", {
          title: `${error}`,
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isFetching = false;
      }
    },

    applyFilter() {
      this.fetchData();
    },

    clearAllFilters() {
      // Resetea todos los filtros a vacío
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = "";
      });

      this.fetchData();
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
  },

  data() {
    return {
      pkid_accreditation_document_detail: [],
      initialItems: [],
      isFetching: true,
      isCollapsed: false,
      perPage: 50,
      pageOptions: [50, 100, 200, 500],
      totalRows: 1,
      currentPage: 1,
      sortBy: "",
      filterName: "",
      filterSurname: "",
      filterDni: "",
      filterMatricula: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      items: [],
      filterOn: [],
      fields: [
        { class: "colModify", key: "Modify", label: "" },
        {
          class: "colStyle",
          key: "pkid_accreditation_document_detail",
          label: "Id",
          sortable: true,
        },
        { class: "colStyle", key: "Nom_titular", label: "Nombre", sortable: true },
        {
          class: "colStyle",
          key: "Cognom1_titular",
          label: "Apellido 1",
          sortable: true,
        },
        {
          class: "colStyle",
          key: "Cognom2_titular",
          label: "Apellido 2",
          sortable: true,
        },
        { class: "colStyle", key: "DOI_Type", label: "DOI", sortable: true },
        { class: "colStyle", key: "DNI", label: "DNI", sortable: true },
        { class: "colStyle", key: "email", label: "Email", sortable: true },
        { class: "colStyle", key: "telefon", label: "Teléfono", sortable: true },
        {
          class: "colStyle",
          key: "Data_neixament",
          label: "Fecha nacimiento",
          sortable: true,
        },

        {
          class: "colStyle",
          key: "Empresa_nom",
          label: "Empresa",
          sortable: true,
        },
        {
          class: "colStyle",
          key: "AccesSCA",
          label: "Aforo-Acceso",
          sortable: true,
        },
        {
          class: "colStyle",
          key: "timestamp_modificacio",
          label: "Fecha Modificación",
          sortable: true,
          formatter: (value) => {
            return value ? moment.utc(value).format("DD/MM/YYYY HH:mm") : "";
          },
        },
        {
          class: "colStyle",
          key: "datetime_start_acreditation",
          label: "Inicio Acreditación",
          sortable: true,
          formatter: (value) => {
            return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
          },
        },
        {
          class: "colStyle",
          key: "datetime_end_acreditation",
          label: "Fin Acreditación",
          sortable: true,
          formatter: (value) => {
            return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
          },
        },
      ],
      filters: {
        nameFilter: "",
        surname1Filter: "",
        surname2Filter: "",
        dniFilter: "",
        matriculaFilter: "",
      },
      filtersConfig: [
        {
          key: "nameFilter",
          label: "Nombre",
          placeholder: "Buscar por nombre",
          param: "Nom_titular",
        },
        {
          key: "surname1Filter",
          label: "Apellido 1",
          placeholder: "Buscar por apellido 1",
          param: "Cognom1_titular",
        },
        {
          key: "surname2Filter",
          label: "Apellido 2",
          placeholder: "Buscar por apellido 2",
          param: "Cognom2_titular",
        },
        {
          key: "dniFilter",
          label: "DNI",
          placeholder: "Buscar por DNI",
          param: "DNI",
        },
        {
          key: "matriculaFilter",
          label: "Matricula/Card",
          placeholder: "Buscar por credencial",
          param: "Matricula",
        },
      ],
    };
  },

  created() {
    this.fetchData();
  },

  mounted() {
    if (localStorage.getItem("accessToken") === null) {
      this.$router.go(0);
    }
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
<style>
.colStyle {
  font-size: small;
}

.colModify {
  /* display: none; */
  width: 0px;
}
</style>
